import axios from 'axios';

// Create axios instance with base URL from environment variables
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: 30000  // 30 second timeout
});

// Add request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    let token = localStorage.getItem('token');
    let refreshToken = localStorage.getItem('refresh_token');

    // ✅ Fallback: extract from Supabase session object
    const supabaseSession = localStorage.getItem('sb-iqpuwixdohfclpenduvm-auth-token');
    if (supabaseSession) {
      try {
        const sessionObj = JSON.parse(supabaseSession);
        const accessToken = sessionObj?.currentSession?.access_token;
        const refresh = sessionObj?.currentSession?.refresh_token;
        if (!token && accessToken) {
          token = accessToken;
        }
        if (!refreshToken && refresh) {
          refreshToken = refresh;
        }
      } catch (e) {
        console.warn('❌ Failed to parse Supabase session object:', e);
      }
    }
    
    const fingerprint = localStorage.getItem('fingerprint');

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    if (refreshToken) {
      config.headers['X-Refresh-Token'] = refreshToken;
    }
    if (fingerprint) {
      config.headers['X-Fingerprint'] = fingerprint;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response) {
      console.error('API Error Response:', {
        status: error.response.status,
        data: error.response.data,
        url: originalRequest?.url
      });

      // Handle 401 errors
      if (error.response.status === 401) {
        if (window.location.pathname !== '/login' && !originalRequest?.url?.includes('/auth/login')) {
          const refreshToken = localStorage.getItem('refresh_token');
          if (refreshToken && !originalRequest._retry) {
            originalRequest._retry = true;
            
            try {
              const { supabase } = await import('./supabaseClient');
              const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
              
              if (!refreshError && session) {
                localStorage.setItem('token', session.access_token);
                localStorage.setItem('refresh_token', session.refresh_token);
                originalRequest.headers['Authorization'] = `Bearer ${session.access_token}`;
                originalRequest.headers['X-Refresh-Token'] = session.refresh_token;
                return api(originalRequest);
              }
            } catch (refreshErr) {
              console.error('Token refresh failed:', refreshErr);
            }
          }
          
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
      }
    } else if (error.request) {
      console.error('API Error Request (Network):', {
        message: error.message,
        url: originalRequest?.url,
        status: 'Network Error'
      });
    } else {
      console.error('API Error Setup:', error.message);
    }

    return Promise.reject(error);
  }
);

// API functions for authentication
export const authAPI = {
  login: (email, password) => {
    return api.post('/api/auth/login', { email, password });
  },
  register: (name, email, password) => {
    return api.post('/api/auth/register', { name, email, password });
  },
  logout: () => {
    return api.post('/api/auth/logout').finally(() => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('refresh_token');
    });
  },
  getCurrentUser: () => {
    return api.get('/api/auth/me');
  },
  getCredits: () => {
    return api.get('/api/auth/credits');
  },
  addCredits: (amount) => {
    return api.post('/api/auth/credits/add', { amount });
  }
};

// API functions for PDP analysis
export const pdpAPI = {
  analyzePDP: async (url) => {
    try {
      console.log('📤 Sending analyze request:', url);
      
      const response = await api.post('/api/analysis/analyze', { url });
      
      console.log('📥 Raw API response:', response);
      
      // Success case
      const analysisData = response.data.data || response.data;
      
      return {
        success: true,
        data: {
          id: analysisData.id || analysisData.analysis_id,
          ...analysisData
        }
      };
      
    } catch (error) {
      console.error('❌ Analysis API error:', error);
      console.error('Error response:', error.response);
      
      // 🔥 FIX: Handle NOT_A_PDP error specially
      if (error.response?.status === 400 && error.response?.data) {
        const errorData = error.response.data;
        
        console.log('🔍 400 Error Data:', errorData);
        
        // Check if it's a NOT_A_PDP error
        if (errorData.code === 'NOT_A_PDP') {
          console.log('🚨 NOT_A_PDP error detected');
          console.log('Details:', errorData.details);
          
          return {
            success: false,
            code: 'NOT_A_PDP',
            error: errorData.message || 'Not a product page',
            message: errorData.message,
            details: errorData.details || {
              detected_type: 'unknown',
              confidence: 0,
              reasons: [],
              suggestions: [
                'Click on a specific product from this page',
                'Look for URLs containing /product/, /item/, or /p/',
                'Make sure the page shows a single Add to Cart button'
              ]
            }
          };
        }
        
        // Other 400 errors
        return {
          success: false,
          code: errorData.code || 'ANALYSIS_ERROR',
          error: errorData.message || 'Analysis failed',
          data: errorData
        };
      }
      
      // Network or other errors
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Analysis failed',
        code: error.response?.data?.code || 'NETWORK_ERROR'
      };
    }
  },
  
  getAnalysisHistory: () => {
    return api.get('/api/analysis/user/analyses');
  },
  
  getAnalysisById: (id) => {
    return api.get(`/api/analysis/${id}`);
  },
  
  claimAnalysis: async (analysisId, fingerprint) => {
    try {
      const response = await api.post('/api/analysis/claim-analysis', {
        analysis_id: analysisId,
        fingerprint: fingerprint
      });

      if (!response.data) {
        throw new Error('No response data received');
      }

      return {
        success: true,
        data: response.data
      };

    } catch (error) {
      console.error('❌ Claim analysis error:', error);
      return {
        success: false,
        error: error.message || 'Failed to claim analysis',
        code: error.response?.data?.code || null
      };
    }
  },
  
  getAnalysisDetails: async (analysisId) => {
    try {
      const response = await api.get(`/api/analysis/${analysisId}`);

      if (!response.data) {
        throw new Error('No response data received');
      }

      return {
        success: true,
        data: response.data.data || response.data
      };

    } catch (error) {
      console.error('❌ Get analysis details error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        code: error.response?.data?.code
      };
    }
  }
};

export const debugTokens = () => {
  const allKeys = Object.keys(localStorage);
  const authKeys = allKeys.filter(key =>
    key.includes('token') ||
    key.includes('auth') ||
    key.includes('sb-')
  );

  authKeys.forEach(key => {
    const value = localStorage.getItem(key);
    console.log(`🔑 ${key}:`, value ? value.substring(0, 50) + '...' : 'empty');
  });

  return {
    keys: authKeys,
    hasAnyToken: authKeys.some(key => localStorage.getItem(key))
  };
};

// API functions for storage (screenshots, etc.)
export const storageAPI = {
  uploadScreenshot: (file, analysisId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('analysisId', analysisId);

    return api.post('/api/storage/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getScreenshot: (id) => {
    return api.get(`/api/storage/${id}`);
  }
};

// ✅ NEW: API functions for structured feedback
export const feedbackAPI = {
  // Submit structured feedback and get 3 bonus credits
  submitStructuredFeedback: (feedbackData) => {
    console.log('📡 feedbackAPI calling:', process.env.REACT_APP_API_URL + '/api/feedback/submit-structured');
    console.log('📡 token present:', !!localStorage.getItem('token'));
    return api.post('/api/feedback/submit-structured', feedbackData);
  },
  
  // Check if user is eligible for feedback bonus
  checkEligibility: () => {
    return api.get('/api/feedback/check-eligibility');
  }
};

export { api };
export default api;